<template>
  <Layout>
    <div v-if="showConfetti" class="confetti-overlay">
      <Vue3Lottie
        :animationData="confettiAnimation"
        :autoPlay="true"
        :loop="false"
        @onComplete="onConfettiComplete"
        ref="confettiRef"
      />
    </div>

    <div class="page-header">
      <div class="page-title">
        <h2 class="page-title-h2">{{$t('promo_page_title')}}</h2>
        <span class="page-title-desc">{{$t('promo_page_description')}}</span>
      </div>
      <div class="page-actions">
        <button
            class="dark-button"
            @click="openCreateModal"
        >
          <img src="../assets/images/add.svg" class="btn-icon">
          {{ $t('new_promo_code') }}
        </button>
      </div>
    </div>

    <div class="promotion-codes-content">
      <PromotionCodesTable
        :items="promotionCodes"
        :loading="isLoading"
        @edit-item="handleEditItem"
        @delete-item="handleDeleteItem"
      />

      <Pagination
        v-if="paginationData && paginationData.total > 0"
        :current-page="currentPage"
        :total-items="paginationData.total"
        :per-page="paginationData.per_page || 20"
        :on-page-change="handlePage"
      />
    </div>

    <PromotionCodeModal
      :isVisible="showPromotionModal"
      :editItem="selectedPromotionItem"
      @close="closePromotionModal"
      @success="handlePromotionSuccess"
    />

    <PromotionSuccessModal
      :isVisible="showSuccessModal"
      :promotionCode="createdPromotionCode"
      @close="closeSuccessModal"
      @view-codes="viewPromotionCodes"
    />
  </Layout>
</template>

<script>
import Layout from "@/components/layouts/Layout.vue";
import PromotionCodesTable from "@/components/tables/PromotionCodesTable.vue";
import Pagination from "@/components/Pagination.vue";
import PromotionCodeModal from "@/components/modals/PromotionCodeModal.vue";
import PromotionSuccessModal from "@/components/modals/PromotionSuccessModal.vue";
import { Vue3Lottie } from 'vue3-lottie';
import confettiData from '@/assets/animations/confetti.json';
import promotionCodeService from "@/service/promotionCodeService";
import { handleErrorResponse } from "@/utils/utils";

export default {
  name: 'PromotionCodes',
  components: {
    Layout,
    PromotionCodesTable,
    Pagination,
    PromotionCodeModal,
    PromotionSuccessModal,
    Vue3Lottie
  },
  data() {
    return {
      isLoading: true,
      promotionCodes: [],
      paginationData: null,
      currentPage: 1,
      showPromotionModal: false,
      selectedPromotionItem: null,
      showSuccessModal: false,
      createdPromotionCode: '',
      showConfetti: false,
      confettiAnimation: confettiData
    }
  },
  async created() {
    await this.loadPromotionCodes();
  },
  methods: {
    async loadPromotionCodes() {
      try {
        this.isLoading = true;
        const params = {
          page: this.currentPage,
          per_page: 20
        };

                const response = await promotionCodeService.getPromotionCodes(params);
        console.log('Promotion Codes Response:', response.data);

        this.paginationData = response.data.data;
        this.promotionCodes = response.data.data.data || [];

        console.log('🔧 First promotion code item structure:', this.promotionCodes[0]);

        this.isLoading = false;
      } catch (error) {
        console.error('Error loading promotion codes:', error);
        handleErrorResponse(error, this.$snackbar);
        this.isLoading = false;
      }
    },

    async handlePage(page) {
      if (this.isLoading && this.currentPage !== 1) return;

      this.currentPage = page;
      await this.loadPromotionCodes();
    },

    handleEditItem(item) {
      console.log('🔧 Edit button clicked for item:', item);
      this.selectedPromotionItem = item;
      this.showPromotionModal = true;
    },

    openCreateModal() {
      this.selectedPromotionItem = null;
      this.showPromotionModal = true;
    },

    closePromotionModal() {
      this.showPromotionModal = false;
      this.selectedPromotionItem = null;
    },

    async handlePromotionSuccess(promotionData) {
      this.closePromotionModal();

      this.showConfetti = true;

      this.createdPromotionCode = promotionData.code || promotionData.promotionCode || 'YENI_KOD';
      this.showSuccessModal = true;

      await this.loadPromotionCodes();
    },

    onConfettiComplete() {
      this.showConfetti = false;
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
      this.createdPromotionCode = '';
    },

    viewPromotionCodes() {
      this.showConfetti = true;
      this.closeSuccessModal();
    },

    async handleDeleteItem(item) {
      try {
        await promotionCodeService.deletePromotionCode(item.uuid || item.id);

        this.$snackbar.add({
          type: 'success',
          text: this.$t('promo_code_removed')
        });

        // Reload the data
        await this.loadPromotionCodes();
      } catch (error) {
        console.error('Error deleting promotion code:', error);
        handleErrorResponse(error, this.$snackbar);
      }
    }
  }
}
</script>

<style scoped>
.confetti-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  z-index: 999999 !important;
  background: rgba(0, 0, 0, 0.1) !important;
  pointer-events: none !important;
}

.confetti-overlay .lottie-container,
.confetti-overlay > div {
  max-width: 100vw;
  max-height: 100vh;
  width: 100%;
  height: 100%;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 20px;
}

.page-title {
  flex: 1;
}

.page-title-h2 {
  font-size: 20px;
  line-height: 28px;
  color: #040C2D;
  font-weight: 500;
  font-family: 'euclid_medium', sans-serif;
  margin: 0 0 4px 0;
}

.page-title-desc {
  font-size: 14px;
  line-height: 20px;
  color: #475467;
  font-weight: 400;
  font-family: 'euclid_medium', sans-serif;
  margin: 0;
}

.page-actions {
  display: flex;
  gap: 12px;
}

.btn-icon {
  width: 16px;
  height: 16px;
  filter: brightness(0) invert(1);
}

.promotion-codes-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: center;
  align-items: center;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .page-actions {
    justify-content: flex-start;
  }
}
</style>
